import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, HelpCircle, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { useProgress } from '../../context/ProgressContext';
import { cn } from '../../lib/utils';

export function Quiz({ moduleId, quiz }) {
  const { progress, saveQuizResult } = useProgress();
  
  // Get saved result and validate it
  const savedResult = progress.quizResults[moduleId];
  
  // Validate saved result - ensure the saved answer matches the expected correct answer
  const isValidSavedResult = savedResult && 
    typeof savedResult.selectedAnswer === 'number' &&
    typeof savedResult.passed === 'boolean' &&
    // Verify the passed status matches whether they selected the correct answer
    savedResult.passed === (savedResult.selectedAnswer === quiz.correctIndex);
  
  const [selectedAnswer, setSelectedAnswer] = useState(
    isValidSavedResult ? savedResult.selectedAnswer : null
  );
  const [showResult, setShowResult] = useState(isValidSavedResult);
  const [isCorrect, setIsCorrect] = useState(
    isValidSavedResult ? savedResult.passed : false
  );

  // Reset state if moduleId changes
  useEffect(() => {
    const result = progress.quizResults[moduleId];
    const isValid = result && 
      typeof result.selectedAnswer === 'number' &&
      typeof result.passed === 'boolean' &&
      result.passed === (result.selectedAnswer === quiz.correctIndex);
    
    if (isValid) {
      setSelectedAnswer(result.selectedAnswer);
      setShowResult(true);
      setIsCorrect(result.passed);
    } else {
      setSelectedAnswer(null);
      setShowResult(false);
      setIsCorrect(false);
    }
  }, [moduleId, quiz.correctIndex, progress.quizResults]);

  const handleSelectAnswer = (index) => {
    if (showResult) return;
    setSelectedAnswer(index);
  };

  const handleSubmit = () => {
    if (selectedAnswer === null) return;
    
    const correct = selectedAnswer === quiz.correctIndex;
    console.log('Quiz submitted:', { moduleId, selectedAnswer, correctIndex: quiz.correctIndex, correct });
    setIsCorrect(correct);
    setShowResult(true);
    saveQuizResult(moduleId, correct, selectedAnswer);
  };

  const handleRetry = () => {
    setSelectedAnswer(null);
    setShowResult(false);
    setIsCorrect(false);
  };

  return (
    <div 
      className="bg-white rounded-2xl border-2 border-stone-200 p-6 md:p-8"
      data-testid={`quiz-${moduleId}`}
    >
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl bg-info/10 flex items-center justify-center flex-shrink-0">
          <HelpCircle className="w-6 h-6 text-info" />
        </div>
        <div>
          <h3 className="font-heading font-bold text-xl text-text mb-1">Quick Quiz</h3>
          <p className="text-text-muted text-sm">Test your understanding</p>
        </div>
      </div>

      <p className="font-heading font-semibold text-lg text-text mb-6">
        {quiz.question}
      </p>

      <div className="space-y-3 mb-6">
        {quiz.options.map((option, index) => {
          const isSelected = selectedAnswer === index;
          const isCorrectOption = index === quiz.correctIndex;
          
          let optionClass = "quiz-option";
          if (showResult) {
            if (isCorrectOption) {
              optionClass += " correct";
            } else if (isSelected && !isCorrectOption) {
              optionClass += " incorrect";
            }
          } else if (isSelected) {
            optionClass += " selected";
          }

          return (
            <button
              key={index}
              data-testid={`quiz-option-${index}`}
              onClick={() => handleSelectAnswer(index)}
              disabled={showResult}
              className={cn(
                "w-full text-left p-4 rounded-xl border-2 transition-all",
                optionClass,
                !showResult && "hover:border-primary cursor-pointer",
                showResult && "cursor-default"
              )}
            >
              <div className="flex items-center gap-3">
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold",
                  showResult && isCorrectOption && "bg-success text-white",
                  showResult && isSelected && !isCorrectOption && "bg-error text-white",
                  !showResult && isSelected && "bg-primary text-white",
                  !showResult && !isSelected && "bg-stone-100 text-text-muted"
                )}>
                  {showResult && isCorrectOption && <CheckCircle className="w-5 h-5" />}
                  {showResult && isSelected && !isCorrectOption && <XCircle className="w-5 h-5" />}
                  {!showResult && String.fromCharCode(65 + index)}
                </div>
                <span className="font-medium text-text">{option}</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Result feedback */}
      {showResult && (
        <div 
          className={cn(
            "rounded-xl p-4 mb-6",
            isCorrect ? "bg-success/10 border border-success/30" : "bg-error/10 border border-error/30"
          )}
          data-testid="quiz-result"
        >
          <div className="flex items-start gap-3">
            {isCorrect ? (
              <CheckCircle className="w-6 h-6 text-success flex-shrink-0 mt-0.5" />
            ) : (
              <XCircle className="w-6 h-6 text-error flex-shrink-0 mt-0.5" />
            )}
            <div>
              <p className={cn(
                "font-bold mb-1",
                isCorrect ? "text-success" : "text-error"
              )}>
                {isCorrect ? "Correct! Well done!" : "Not quite right"}
              </p>
              <p className="text-text-muted text-sm">{quiz.explanation}</p>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        {!showResult ? (
          <Button
            data-testid="quiz-submit"
            onClick={handleSubmit}
            disabled={selectedAnswer === null}
            className="bg-primary text-white hover:bg-primary-600 rounded-full px-8 py-6 text-lg font-bold"
          >
            Check Answer
          </Button>
        ) : !isCorrect && (
          <Button
            data-testid="quiz-retry"
            onClick={handleRetry}
            variant="outline"
            className="rounded-full px-6 py-4 font-semibold"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        )}
      </div>
    </div>
  );
}
